// ═══════════════════════════════════════════════════════════════
// Impact — Self-Report Review Queue
// Staff review, triage, and approve/reject citizen self-reports
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, Select,
  TextInput, Textarea, Modal, Divider, ActionIcon, Tooltip,
  Alert, Loader, Center, Image,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconCheck, IconX, IconEye, IconAlertTriangle,
  IconClipboardList, IconUser, IconPhone, IconMail, IconMapPin,
  IconHome, IconRefresh, IconPhoto,
} from '@tabler/icons-react';
import { useImpactStore, type Assessment, type AssessmentPhoto } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { fmtDate } from '../../lib/dateFormat';

// ── Helpers ────────────────────────────────────────────────────

function parseExtra(notes: string): { phone: string; livingSituation: string; insured: string; floodInsurance: string; damageScore: string } {
  try { return { phone: '', livingSituation: '', insured: '', floodInsurance: '', damageScore: '', ...JSON.parse(notes) }; }
  catch { return { phone: '', livingSituation: '', insured: '', floodInsurance: '', damageScore: '' }; }
}

const CATEGORY_COLORS: Record<string, string> = {
  affected: 'var(--color-success)', minor: '#C68700', major: '#F86A0B', destroyed: 'var(--color-error)', inaccessible: 'var(--data-viz-650)',
};

const STATUS_BADGE: Record<string, { color: string; label: string }> = {
  pending:  { color: '#C68700', label: 'Pending Review' },
  approved: { color: 'var(--color-success)',  label: 'Approved' },
  rejected: { color: 'var(--color-error)',    label: 'Rejected' },
  active:   { color: 'var(--color-primary-700)',   label: 'Approved' },
};

// ── Main Component ─────────────────────────────────────────────

export default function SelfReportReview() {
  const { assessments, loading, loadAssessments, updateAssessment } = useImpactStore();
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('impact');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>('pending');
  const [selected, setSelected] = useState<Assessment | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editCategory, setEditCategory] = useState('');
  const [photos, setPhotos] = useState<AssessmentPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  const loadPhotosForSelected = useCallback(async (assessmentId: number) => {
    setPhotos([]);
    setPhotosLoading(true);
    try {
      const p = await useImpactStore.getState().loadPhotos(assessmentId);
      setPhotos(p);
    } finally {
      setPhotosLoading(false);
    }
  }, []);

  // All self-reports for the active incident
  const selfReports = useMemo(() => {
    return assessments.filter(a => {
      if (a.assessmentType !== 'self_report') return false;
      if (activeIncident && String(a.incidentId) !== String(activeIncident.id)) return false;
      return true;
    });
  }, [assessments, activeIncident]);

  // Apply search + status filter
  const filtered = useMemo(() => {
    let list = selfReports;
    if (statusFilter) list = list.filter(a => {
      if (statusFilter === 'pending') return a.status === 'pending' || !a.status;
      if (statusFilter === 'approved') return a.status === 'active' || a.status === 'approved';
      if (statusFilter === 'rejected') return a.status === 'rejected';
      return true;
    });
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        (a.assessorName || '').toLowerCase().includes(q) ||
        (a.address || '').toLowerCase().includes(q) ||
        (a.assessorEmail || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [selfReports, statusFilter, search]);

  // Stats
  const pending = selfReports.filter(a => a.status === 'pending' || !a.status).length;
  const approved = selfReports.filter(a => a.status === 'active' || a.status === 'approved').length;
  const rejected = selfReports.filter(a => a.status === 'rejected').length;

  // ── Actions ────────────────────────────────────────────────

  const handleApprove = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await updateAssessment(selected.id, {
        assessmentType: 'individual',
        status: 'active',
        damageCategory: editCategory || selected.damageCategory,
      });
      setSelected(null);
      setEditCategory('');
    } finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const extra = parseExtra(selected.notes || '');
      const updatedNotes = JSON.stringify({ ...extra, rejectionReason: rejectReason });
      await updateAssessment(selected.id, { status: 'rejected', notes: updatedNotes });
      setSelected(null);
      setRejectReason('');
    } finally { setActionLoading(false); }
  };

  const openDetail = (a: Assessment) => {
    setSelected(a);
    setEditCategory(a.damageCategory || '');
    setRejectReason('');
    loadPhotosForSelected(a.id);
  };

  // ── Loading ────────────────────────────────────────────────

  if (loading && selfReports.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  if (!activeIncident) {
    return (
      <Alert color="var(--color-primary-700)" icon={<IconAlertTriangle size={18} />} title="No Active Incident">
        Select an active incident from the top nav to view self-reports.
      </Alert>
    );
  }

  // ── Render ─────────────────────────────────────────────────

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="xs">
          <Title order={3} c="black">Self-Report Review</Title>
          <Badge size="sm" variant="light" color="var(--color-primary-700)">{activeIncident.name}</Badge>
        </Group>
        <Tooltip label="Refresh">
          <ActionIcon variant="light" size="sm" onClick={() => loadAssessments()}>
            <IconRefresh size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Stat cards */}
      <Group gap="sm">
        <Paper p="sm" withBorder ta="center" style={{ flex: 1, borderTop: '3px solid var(--color-warning)' }}>
          <Text fw={700} size="xl">{pending}</Text>
          <Text size="xs" c="dimmed">Pending</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ flex: 1, borderTop: '3px solid var(--color-success)' }}>
          <Text fw={700} size="xl">{approved}</Text>
          <Text size="xs" c="dimmed">Approved</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ flex: 1, borderTop: '3px solid var(--color-error)' }}>
          <Text fw={700} size="xl">{rejected}</Text>
          <Text size="xs" c="dimmed">Rejected</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ flex: 1, borderTop: '3px solid var(--color-neutral-400)' }}>
          <Text fw={700} size="xl">{selfReports.length}</Text>
          <Text size="xs" c="dimmed">Total</Text>
        </Paper>
      </Group>

      {/* Filters */}
      <Paper p="md" radius="sm" withBorder>
        <Group gap="sm">
          <TextInput
            placeholder="Search name, address, email..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
            size="sm"
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'pending', label: 'Pending Review' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
              { value: '', label: 'All' },
            ]}
            clearable={false}
            size="sm"
            w={160}
          />
        </Group>
      </Paper>

      {/* Report list */}
      {filtered.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <IconClipboardList size={36} color="var(--color-neutral-400)" style={{ margin: '0 auto' }} />
          <Text c="dimmed" mt="sm">
            {statusFilter === 'pending' ? 'No pending self-reports.' : 'No reports match your filters.'}
          </Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {filtered.map(a => {
            const extra = parseExtra(a.notes || '');
            const statusInfo = STATUS_BADGE[a.status || 'pending'] || STATUS_BADGE.pending;
            return (
              <Paper
                key={a.id}
                p="md"
                withBorder
                radius="sm"
                style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onClick={() => openDetail(a)}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--color-primary-100)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <IconUser size={18} color="var(--color-primary-700)" />
                    </div>
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Group gap="xs">
                        <Text fw={600} size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.assessorName || 'Anonymous'}
                        </Text>
                        {a.damageCategory && (
                          <Badge size="xs" color={CATEGORY_COLORS[a.damageCategory] || '#939598'} tt="capitalize">
                            {a.damageCategory}
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <IconMapPin size={11} style={{ marginRight: 2, verticalAlign: 'middle' }} />
                        {[a.address, a.city, a.state].filter(Boolean).join(', ') || 'No address'}
                      </Text>
                      {extra.phone && (
                        <Text size="xs" c="dimmed">
                          <IconPhone size={11} style={{ marginRight: 2, verticalAlign: 'middle' }} />
                          {extra.phone}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                  <Group gap="xs" style={{ flexShrink: 0 }}>
                    <Text size="xs" c="dimmed">{fmtDate(a.datecreated)}</Text>
                    <Badge size="sm" color={statusInfo.color}>{statusInfo.label}</Badge>
                    <ActionIcon variant="light" size="sm" onClick={e => { e.stopPropagation(); openDetail(a); }}>
                      <IconEye size={14} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Detail modal */}
      {/* Lightbox */}
      <Modal
        opened={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
        size="xl"
        title="Photo"
        style={{ zIndex: 1300 }}
      >
        {lightboxSrc && (
          <Image src={lightboxSrc} fit="contain" style={{ maxHeight: '75vh', width: '100%' }} />
        )}
      </Modal>

      <Modal
        opened={!!selected}
        onClose={() => { setSelected(null); setPhotos([]); }}
        title={`Self-Report — ${selected?.assessorName || 'Anonymous'}`}
        size="lg"
      >
        {selected && (() => {
          const extra = parseExtra(selected.notes || '');
          const statusInfo = STATUS_BADGE[selected.status || 'pending'] || STATUS_BADGE.pending;
          const isPending = selected.status === 'pending' || !selected.status;

          return (
            <Stack gap="md">
              {/* Status */}
              <Group>
                <Badge size="md" color={statusInfo.color}>{statusInfo.label}</Badge>
                <Text size="xs" c="dimmed">Submitted {fmtDate(selected.datecreated)}</Text>
              </Group>

              {/* Reporter info */}
              <Paper p="sm" radius="sm" withBorder style={{ background: 'var(--color-neutral-50)' }}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">Reporter Information</Text>
                <Stack gap={4}>
                  <Group gap="xs">
                    <IconUser size={13} color="var(--color-neutral-500)" />
                    <Text size="sm">{selected.assessorName || '—'}</Text>
                  </Group>
                  {selected.assessorEmail && (
                    <Group gap="xs">
                      <IconMail size={13} color="var(--color-neutral-500)" />
                      <Text size="sm">{selected.assessorEmail}</Text>
                    </Group>
                  )}
                  {extra.phone && (
                    <Group gap="xs">
                      <IconPhone size={13} color="var(--color-neutral-500)" />
                      <Text size="sm">{extra.phone}</Text>
                    </Group>
                  )}
                </Stack>
              </Paper>

              {/* Property info */}
              <Paper p="sm" radius="sm" withBorder style={{ background: 'var(--color-neutral-50)' }}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">Property</Text>
                <Stack gap={4}>
                  <Group gap="xs">
                    <IconMapPin size={13} color="var(--color-neutral-500)" />
                    <Text size="sm">{[selected.address, selected.city, selected.state, selected.zip].filter(Boolean).join(', ')}</Text>
                  </Group>
                  {selected.occupancyType && (
                    <Group gap="xs">
                      <IconHome size={13} color="var(--color-neutral-500)" />
                      <Text size="sm">{selected.occupancyType}</Text>
                      {selected.homeCount > 1 && <Text size="xs" c="dimmed">({selected.homeCount} occupants)</Text>}
                    </Group>
                  )}
                </Stack>
              </Paper>

              {/* Damage info */}
              <Paper p="sm" radius="sm" withBorder style={{ background: 'var(--color-neutral-50)' }}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">Damage Assessment</Text>
                <Group gap="md" wrap="wrap">
                  {selected.damageCategory && (
                    <div>
                      <Text size="xs" c="dimmed">Damage Level</Text>
                      <Badge color={CATEGORY_COLORS[selected.damageCategory] || '#939598'} tt="capitalize">
                        {selected.damageCategory}
                      </Badge>
                    </div>
                  )}
                  {extra.damageScore && (
                    <div>
                      <Text size="xs" c="dimmed">Score</Text>
                      <Text size="sm" fw={600}>{extra.damageScore} pts</Text>
                    </div>
                  )}
                  {extra.livingSituation && (
                    <div>
                      <Text size="xs" c="dimmed">Currently Staying</Text>
                      <Text size="sm" tt="capitalize">{extra.livingSituation.replace(/_/g, ' ')}</Text>
                    </div>
                  )}
                  {extra.insured && (
                    <div>
                      <Text size="xs" c="dimmed">Insurance</Text>
                      <Text size="sm" tt="capitalize">{extra.insured}</Text>
                    </div>
                  )}
                  {extra.floodInsurance && (
                    <div>
                      <Text size="xs" c="dimmed">Flood Insurance</Text>
                      <Text size="sm" tt="capitalize">{extra.floodInsurance}</Text>
                    </div>
                  )}
                </Group>
                {selected.description && (
                  <Box mt="sm">
                    <Text size="xs" c="dimmed" mb={2}>Notes from Reporter</Text>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selected.description}</Text>
                  </Box>
                )}
              </Paper>

              {/* Photos */}
              <Paper p="sm" radius="sm" withBorder style={{ background: 'var(--color-neutral-50)' }}>
                <Group gap="xs" mb="xs">
                  <IconPhoto size={13} color="var(--color-neutral-500)" />
                  <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                    Submitted Photos {!photosLoading && `(${photos.length})`}
                  </Text>
                </Group>
                {photosLoading ? (
                  <Group gap="xs">
                    <Loader size="xs" />
                    <Text size="xs" c="dimmed">Loading photos...</Text>
                  </Group>
                ) : photos.length === 0 ? (
                  <Text size="xs" c="dimmed">No photos submitted</Text>
                ) : (
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {photos.map((p, i) => (
                      <Box
                        key={p.id}
                        style={{ position: 'relative', cursor: 'pointer' }}
                        onClick={() => setLightboxSrc(p.photoUrl)}
                      >
                        <Image
                          src={p.photoUrl}
                          w={90} h={90}
                          radius="sm"
                          fit="cover"
                          style={{ display: 'block', border: '1px solid var(--color-neutral-200)' }}
                        />
                        {p.caption && p.caption !== `Photo ${i + 1}` && (
                          <Text size="xs" c="dimmed" mt={2} style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.caption}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>

              {/* Actions (only for pending + editors) */}
              {canEdit && isPending && (
                <>
                  <Divider />
                  <Stack gap="sm">
                    <Text fw={600} size="sm">Review Actions</Text>

                    {/* Category override for approval */}
                    <Select
                      label="Confirm Damage Category"
                      description="Adjust if needed before approving"
                      value={editCategory || null}
                      onChange={v => setEditCategory(v || '')}
                      data={[
                        { value: 'affected', label: 'Affected' },
                        { value: 'minor', label: 'Minor' },
                        { value: 'major', label: 'Major' },
                        { value: 'destroyed', label: 'Destroyed' },
                        { value: 'inaccessible', label: 'Inaccessible' },
                      ]}
                      clearable
                      size="sm"
                    />

                    <Group justify="space-between">
                      {/* Reject */}
                      <Stack gap="xs" style={{ flex: 1 }}>
                        <Textarea
                          placeholder="Rejection reason (optional)"
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                          rows={2}
                          size="sm"
                        />
                        <Button
                          color="var(--color-error)"
                          variant="light"
                          leftSection={<IconX size={14} />}
                          loading={actionLoading}
                          onClick={handleReject}
                          size="sm"
                        >
                          Reject
                        </Button>
                      </Stack>

                      <Divider orientation="vertical" mx="sm" />

                      {/* Approve */}
                      <Button
                        color="var(--color-success)"
                        leftSection={<IconCheck size={14} />}
                        loading={actionLoading}
                        onClick={handleApprove}
                        size="sm"
                        style={{ alignSelf: 'flex-end' }}
                      >
                        Approve as Assessment
                      </Button>
                    </Group>
                  </Stack>
                </>
              )}
            </Stack>
          );
        })()}
      </Modal>
    </Stack>
  );
}

// ── Inline Box shim ────────────────────────────────────────────
function Box({ children, mt, style }: { children: React.ReactNode; mt?: string; style?: React.CSSProperties }) {
  return <div style={{ marginTop: mt, ...style }}>{children}</div>;
}
